import { Logger } from './Logger';

export type PersistedGameDataV1 = {
  version: 1;
  highScore: number;
  muted: boolean;
  skipIntro: boolean;
  reducedEffects: boolean;
};

const STORAGE_KEY = 'retro-space-fight:v1';
export const DEFAULT_DATA: PersistedGameDataV1 = {
  version: 1,
  highScore: 0,
  muted: false,
  skipIntro: false,
  reducedEffects: false,
};

const isValid = (value: unknown): value is PersistedGameDataV1 => {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  return (
    data.version === 1 &&
    Number.isInteger(data.highScore) &&
    Number(data.highScore) >= 0 &&
    typeof data.muted === 'boolean' &&
    typeof data.skipIntro === 'boolean' &&
    typeof data.reducedEffects === 'boolean'
  );
};

export class StorageService {
  private memory = { ...DEFAULT_DATA };

  public constructor(
    private readonly storage: Pick<Storage, 'getItem' | 'setItem'> | undefined,
    private readonly logger: Logger,
  ) {}

  public load(): PersistedGameDataV1 {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) return { ...this.memory };
      const parsed: unknown = JSON.parse(raw);
      if (!isValid(parsed)) return { ...DEFAULT_DATA };
      this.memory = { ...parsed };
    } catch (error) {
      this.logger.warn('Storage read failed; using memory fallback.', error);
    }
    return { ...this.memory };
  }

  public save(patch: Partial<Omit<PersistedGameDataV1, 'version'>>): PersistedGameDataV1 {
    this.memory = { ...this.memory, ...patch, version: 1 };
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.memory));
    } catch (error) {
      this.logger.warn('Storage write failed; retaining memory copy.', error);
    }
    return { ...this.memory };
  }
}
